import {createAsyncThunk} from "@reduxjs/toolkit";
import {fetchUserAttributes, getCurrentUser} from "aws-amplify/auth";
import { fetchUserInfo } from "../../../models/account/accountDataServices";
import { getNewTokenValueFromAWS } from "../../../functions/AmplifyFunctions";
import {setAuthStatus, setEmailAddress, setLoginState, setUserInfo, setUsername} from "../slices";

function normalizeEmailAddress(value) {
    if (typeof value !== "string") {
        return null;
    }

    const trimmedValue = value.trim().toLowerCase();
    if (!trimmedValue.includes("@")) {
        return null;
    }

    return trimmedValue;
}

function isGoogleFederatedUsername(value) {
    return typeof value === "string" && value.toLowerCase().startsWith("google_");
}

async function resolveEmailAddress(currentUser) {
    const loginId = currentUser?.signInDetails?.loginId ?? currentUser?.username ?? currentUser?.userId;
    const normalizedLoginId = normalizeEmailAddress(loginId);
    if (normalizedLoginId) {
        return normalizedLoginId;
    }

    if (isGoogleFederatedUsername(currentUser?.username)) {
        try {
            await getNewTokenValueFromAWS();
        } catch (error) {
            // Fall back to the current session attributes below.
        }
    }

    try {
        const attributes = await fetchUserAttributes();
        return normalizeEmailAddress(attributes?.email);
    } catch (error) {
        return null;
    }
}

export const bootstrapAuthSession = createAsyncThunk(
    "auth/bootstrapSession",
    async (_, {dispatch, getState}) => {
        dispatch(setAuthStatus("checking"));
        try {
            const response = await getCurrentUser();
            const emailAddress = await resolveEmailAddress(response);
            dispatch(setLoginState(true));
            if (emailAddress) {
                dispatch(setEmailAddress(emailAddress));
            }
            return {loggedIn: true, userId: response.userId};
        } catch (err) {
            const authState = getState?.()?.loginToken;
            const storedAccessToken = authState?.userTokens?.accessToken ?? null;
            if (storedAccessToken) {
                const storedEmailAddress = normalizeEmailAddress(authState?.emailAddress);
                dispatch(setLoginState(true));
                if (storedEmailAddress) {
                    dispatch(setEmailAddress(storedEmailAddress));
                }
                return {loggedIn: true, userId: null, source: "vuplex-token"};
            }
            dispatch(setLoginState(false));
            return {loggedIn: false};
        }
    }
);

export const loadLoggedInAppData = createAsyncThunk(
    "auth/loadLoggedInAppData",
    async ({setIdentity} = {}, {dispatch}) => {
        const userInfoResponse = await fetchUserInfo({ syncUserInfo: false });

        dispatch(setUserInfo(userInfoResponse));
        dispatch(setUsername(userInfoResponse.userName));
        dispatch(setEmailAddress(normalizeEmailAddress(userInfoResponse?.email)));

        if (setIdentity && userInfoResponse?.email) {
            setIdentity(userInfoResponse.email);
        }

        return userInfoResponse;
    }
);
